import "leaflet/dist/leaflet.css";
import "leaflet-draw/dist/leaflet.draw.css";
import "./styles.css";
import {
  useState,
  useMemo,
  useLayoutEffect,
  useEffect,
  useCallback,
} from "react";
import { useSelector, useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { dateLocale, useLocalize } from "../../i18n";
import MapComponent from "./Map";
import {
  setTiles,
  toggleTileVisibility,
  setImageDate,
  setAOILayer,
  setCropTypes,
  setFieldsLegends,
  setIrrigationMethods,
  setMapBounds,
  setSatelliteTilesAvailable,
  setTilesLimits,
  setTileBan,
  clearTileBan,
  setOverlayMode,
  getEffectiveBaseLayer,
  setPlanetTileTemplate,
  setPlanetTileFailed,
  setDefaultMapTileTemplate,
} from "../../reducers/MapSlice";
import {
  TILE_REFRESH_MARGIN_SECONDS,
  nowSeconds,
} from "../../hooks/useTileTemplateRefresh";
import {
  getAOIGeoJSON,
  getIrrigationMethods,
  getSatelliteTilesAvailable,
  getTileBanStatus,
  getPlanetTileTemplate,
} from "../../api/FlaskAPI";
import { toast } from "react-toastify";
import NavigationPanel from "../../components/NavigationPanel";
import "react-photo-view/dist/react-photo-view.css";
import {
  getIndexTile,
  getProjectBounds,
  getCropTypes,
  getTilesLimits,
  getFieldsLegends,
  getDefaultMapTileTemplate,
} from "../../api/FlaskAPI";
import { getErrorMessage } from "../../api/errors";
import { newId } from "../../utils/id";

import PanelSwitcher from "../../components/PanelSwitcher";
import MapLegend from "../../components/MapLegend";
import { NARROW_SCREEN } from "../../utils/layout";
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  useMediaQuery,
  Typography,
} from "@mui/material";
import IndexLegend from "../../components/IndexLegend";
import AvailableDatePicker from "../../components/AvailableDatePicker";
import DateRangeDialog from "../../components/DateRangeDialog";

const TILE_BAN_TICK_SECONDS = 5;
const TILE_BAN_RECHECK_MILLISECONDS = 60000;

const formatRemainingTime = (seconds, t) =>
  t("map.ban.time", {
    m: Math.floor(seconds / 60),
    s: (seconds % 60).toString().padStart(2, "0"),
  });

export default function BasePage() {
  const userProjectName = useSelector((state) => state.auth.projectName);
  const mapBounds = useSelector((state) => state.mapState.mapBounds);
  const aoiLayer = useSelector((state) => state.mapState.staticLayers.aoi);

  const dispatch = useDispatch();
  const { t, i18n } = useTranslation();
  const localize = useLocalize();
  const [submitLoading, setSubmitLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(!(mapBounds && aoiLayer));

  // On narrow screens only one side panel is open at a time.
  const narrow = useMediaQuery(NARROW_SCREEN);
  const [leftFolded, setLeftFolded] = useState(false);
  const [rightFolded, setRightFolded] = useState(
    () => window.matchMedia(NARROW_SCREEN).matches,
  );
  const foldLeft = (fold) => {
    setLeftFolded(fold);
    if (!fold && narrow) setRightFolded(true);
  };
  const foldRight = (fold) => {
    setRightFolded(fold);
    if (!fold && narrow) setLeftFolded(true);
  };
  useEffect(() => {
    if (narrow && !leftFolded && !rightFolded) setRightFolded(true);
  }, [narrow, leftFolded, rightFolded]);

  const tileBan = useSelector((state) => state.mapState.tileBan);
  const [remainingSeconds, setRemainingSeconds] = useState(null);
  const [checkingBan, setCheckingBan] = useState(false);

  const setInitailDate = useCallback(async () => {
    try {
      const res = await getTileBanStatus();
      if (res.data.banned) {
        setRemainingSeconds(res.data.remaining_seconds);
        if (!tileBan.banned) {
          dispatch(
            setTileBan({
              message: res.data.message,
              remainingSeconds: res.data.remaining_seconds,
            }),
          );
        }
      } else {
        setRemainingSeconds(0);
        // Nothing clears the banner while banned, so the lift happen here.
        if (tileBan.banned) dispatch(clearTileBan());
      }
    } catch (error) {
      setRemainingSeconds(0);
    }
  }, [dispatch, tileBan.banned]);

  useEffect(() => {
    setInitailDate();
  }, [tileBan.banned, setInitailDate, dispatch]);

  const formatedRemainingTime =
    remainingSeconds !== null
      ? formatRemainingTime(remainingSeconds, t)
      : t("map.ban.time", { m: "--", s: "--" });

  // remainingSeconds is not a dependency: it changes every tick, and
  // rebuilding the effect restarted the 60s recheck before it could fire.
  useEffect(() => {
    if (!tileBan.banned) return;
    const timer = setInterval(() => {
      setRemainingSeconds((prev) =>
        prev === null ? prev : Math.max(prev - TILE_BAN_TICK_SECONDS, 0),
      );
    }, TILE_BAN_TICK_SECONDS * 1000);
    const resetTimer = setInterval(
      setInitailDate,
      TILE_BAN_RECHECK_MILLISECONDS,
    );
    return () => {
      clearInterval(timer);
      clearInterval(resetTimer);
    };
  }, [tileBan.banned, setInitailDate]);

  // Countdown hit zero: ask the server right away instead of waiting.
  useEffect(() => {
    if (tileBan.banned && remainingSeconds === 0) setInitailDate();
  }, [tileBan.banned, remainingSeconds, setInitailDate]);

  const handleLiftBan = async () => {
    if (checkingBan) return;
    setCheckingBan(true);
    try {
      const res = await getTileBanStatus();
      if (res.data.banned) {
        dispatch(
          setTileBan({
            message: res.data.message,
            remainingSeconds: res.data.remaining_seconds,
          }),
        );
      } else {
        dispatch(clearTileBan());
      }
    } catch (error) {
      const status = error.response?.status;
      const data = error.response?.data || {};
      if (status === 403) {
        dispatch(
          setTileBan({
            message: data.message || t("map.ban.blocked"),
            remainingSeconds: data.remaining_seconds,
          }),
        );
      }
    } finally {
      setCheckingBan(false);
    }
  };

  // Fetch static GeoJSON layer (AOI) once on BasePage mount
  useEffect(() => {
    if (!userProjectName) return;
    // Guard: skip refetch and loading if static layers already loaded in Redux
    if (mapBounds && aoiLayer) {
      setPageLoading(false);
      return;
    }

    const fetchStaticLayers = async () => {
      try {
        const [
          aoiResponse,
          boundsResponse,
          cropTypesResponse,
          fieldsLegendsResponse,
          irrigationMethodsResponse,
          tilesLimits,
          satelliteTilesResponse,
        ] = await Promise.all([
          getAOIGeoJSON(),
          getProjectBounds(),
          getCropTypes(),
          getFieldsLegends(),
          getIrrigationMethods(),
          getTilesLimits(),
          getSatelliteTilesAvailable().catch(() => ({
            data: {
              satellite_tiles: false,
              planet_tiles: false,
              defaultmap_tiles: false,
            },
          })),
        ]);

        const isSatelliteTilesAvailable = satelliteTilesResponse.data;
        dispatch(setSatelliteTilesAvailable(isSatelliteTilesAvailable));

        const defaultmapTemplateResponse =
          await getDefaultMapTileTemplate().catch(() => null);
        if (defaultmapTemplateResponse?.status === 200) {
          dispatch(
            setDefaultMapTileTemplate({
              tileUrlTemplate:
                defaultmapTemplateResponse.data.tile_url_template,
              attribution: defaultmapTemplateResponse.data.attribution,
              expiresAt: defaultmapTemplateResponse.data.expires_at,
            }),
          );
        }

        if (aoiResponse.status === 200) {
          dispatch(setAOILayer(aoiResponse.data));
        }

        if (boundsResponse.status === 200) {
          dispatch(setMapBounds(boundsResponse.data));
        } else {
          toast.error(t("map.loadFailed.bounds"));
          throw new Error("Failed to load project bounds");
        }
        if (cropTypesResponse.status === 200) {
          dispatch(setCropTypes(cropTypesResponse.data));
        } else {
          toast.error(t("map.loadFailed.crops"));
          throw new Error("Failed to load crop types");
        }
        if (fieldsLegendsResponse.status === 200) {
          const first_key = fieldsLegendsResponse.data?.[0]?.key;
          if (!!first_key) {
            dispatch(setOverlayMode(first_key));
          }
          dispatch(setFieldsLegends(fieldsLegendsResponse.data));
        } else {
          toast.error(t("map.loadFailed.legends"));
          throw new Error("Failed to load fields legends");
        }
        if (irrigationMethodsResponse.status === 200) {
          dispatch(setIrrigationMethods(irrigationMethodsResponse.data));
        } else {
          toast.error(t("map.loadFailed.irrigation"));
          throw new Error("Failed to load irrigation methods");
        }
        if (tilesLimits.status === 200) {
          dispatch(setTilesLimits(tilesLimits.data));
        } else {
          toast.error(t("map.loadFailed.tilesLimits"));
          throw new Error("Failed to load tiles limits");
        }
      } catch (error) {
        console.error("Error fetching static layers:", error);
        toast.error(t("map.loadFailed.bounds"));
        throw error;
      } finally {
        setPageLoading(false);
      }
    };

    fetchStaticLayers();
  }, [userProjectName, dispatch]);

  // date range dialog state
  const [rangeDialogOpen, setRangeDialogOpen] = useState(false);

  //map states - workspaces
  const currentIndex = useSelector((state) => state.categories?.currentIndex);
  const workspaces = useSelector((state) => state.mapState.workspaces);
  const currentWorkspace = useSelector(
    (state) => state.mapState.currentWorkspace,
  );
  const tilesLimits = useSelector((state) => state.mapState.tilesLimits);
  const tiles = workspaces[currentWorkspace];
  const drawingMode = useSelector((state) => state.mapState.drawingMode);
  const boundaryMethod = useSelector((state) => state.mapState.boundaryMethod);
  const tileLayer = useSelector((state) => state.mapState.tileLayer);
  const satelliteLayer = useSelector((state) => state.mapState.satelliteLayer);
  const planetLayer = useSelector((state) => state.mapState.planetLayer);
  const defaultmapLayer = useSelector(
    (state) => state.mapState.defaultmapLayer,
  );
  const satelliteTilesAvailable = useSelector(
    (state) => state.mapState.satelliteTilesAvailable,
  );

  const isDrawing = boundaryMethod === "draw";
  const planetEnabled =
    drawingMode &&
    isDrawing &&
    satelliteTilesAvailable.planetTiles &&
    !tileBan.banned;

  // Fetch Planet template when the user starts drawing and Planet tiles are available.
  useEffect(() => {
    if (!planetEnabled) {
      dispatch(setPlanetTileFailed(false));
      return;
    }
    const isFresh =
      planetLayer.tileUrlExpiresAt - TILE_REFRESH_MARGIN_SECONDS > nowSeconds();
    if (isFresh && planetLayer.tileUrl) return;

    let cancelled = false;
    getPlanetTileTemplate()
      .then((res) => {
        if (cancelled) return;
        dispatch(
          setPlanetTileTemplate({
            tileUrlTemplate: res.data.tile_url_template,
            attribution: res.data.attribution,
            expiresAt: res.data.expires_at,
          }),
        );
      })
      .catch((error) => {
        if (cancelled) return;
        console.error("Error fetching planet tile template:", error);
        dispatch(setPlanetTileFailed(true));
      });
    return () => {
      cancelled = true;
    };
  }, [
    planetEnabled,
    planetLayer.tileUrl,
    planetLayer.tileUrlExpiresAt,
    dispatch,
  ]);

  const { disableSatellite, effectiveLayer, toggleLayer } = useMemo(
    () =>
      getEffectiveBaseLayer({
        tileLayer,
        tileBanned: tileBan.banned,
        isDrawing,
        drawingMode,
        planetAvailable: satelliteTilesAvailable.planetTiles,
        planetTileUrl: planetLayer.tileUrl,
        planetFailed: planetLayer.failed,
        satelliteAvailable: satelliteTilesAvailable.satelliteTiles,
        satelliteTileUrl: satelliteLayer.tileUrl,
        defaultmapAvailable: satelliteTilesAvailable.defaultmapTiles,
        defaultmapTileUrl: defaultmapLayer.tileUrl,
      }),
    [
      tileLayer,
      tileBan.banned,
      isDrawing,
      drawingMode,
      satelliteTilesAvailable.planetTiles,
      planetLayer.tileUrl,
      planetLayer.failed,
      satelliteTilesAvailable.satelliteTiles,
      satelliteLayer.tileUrl,
      satelliteTilesAvailable.defaultmapTiles,
      defaultmapLayer.tileUrl,
    ],
  );

  // Load visibility state from localStorage when tiles change
  useEffect(() => {
    if (tiles.length > 0) {
      const savedVisibility = localStorage.getItem(
        `tileVisibilityState_${currentWorkspace}`,
      );
      if (savedVisibility) {
        const visibilityMap = JSON.parse(savedVisibility);
        tiles.forEach((tile) => {
          if (
            visibilityMap[tile.tileId] !== undefined &&
            tile.visible !== visibilityMap[tile.tileId]
          ) {
            dispatch(
              toggleTileVisibility({
                tileId: tile.tileId,
                forceValue: visibilityMap[tile.tileId],
              }),
            );
          }
        });
      }
    }
  }, [tiles.length, currentWorkspace]);

  const imageDate = useSelector((state) => state.mapState.date.imageDate);

  // Extract legends and unit for the map legend component
  const legends = currentIndex?.legends;
  const unit = localize(currentIndex, "unit");

  // Extract available dates from tifs_dates
  const getAvailableDates = () => {
    if (
      !currentIndex ||
      !currentIndex.tifs_dates ||
      currentIndex.tifs_dates.length === 0
    ) {
      return [];
    }

    const dates = currentIndex.tifs_dates.map((date) => {
      return new Date(date);
    });
    return dates;
  };

  const availableDates = getAvailableDates();
  const indexMinDate = availableDates.length > 0 ? availableDates[0] : null;
  const indexMaxDate =
    availableDates.length > 0
      ? availableDates[availableDates.length - 1]
      : null;

  // Check if selected date is valid (must be in availableDates)
  const isDateValid = () => {
    if (!imageDate) return true; // No date selected is valid
    const selectedDate = new Date(imageDate);
    selectedDate.setHours(0, 0, 0, 0);
    return availableDates.some((date) => {
      const d = new Date(date);
      d.setHours(0, 0, 0, 0);
      return d.getTime() === selectedDate.getTime();
    });
  };

  const dateError = !isDateValid();

  // Format date range for display
  const formatAvailableDateRange = () => {
    if (indexMinDate && indexMaxDate) {
      const formatDate = (date) => {
        return date.toLocaleDateString(dateLocale(i18n.resolvedLanguage), {
          year: "numeric",
          month: "short",
          day: "numeric",
        });
      };
      return t("map.available", {
        from: formatDate(indexMinDate),
        to: formatDate(indexMaxDate),
      });
    }
    return "";
  };

  // Initialize imageDate to max available date when index changes
  useEffect(() => {
    if (availableDates.length > 0 && !imageDate) {
      const maxDate = availableDates[availableDates.length - 1];
      const dateStr = maxDate.toISOString().split("T")[0];
      dispatch(setImageDate(dateStr));
    }
  }, [availableDates, imageDate, dispatch]);

  useLayoutEffect(() => {
    return () => {
      // Map state is deliberately kept on unmount; only the toasts go.
      toast.dismiss();
    };
  }, []);

  /**
   * fetches the stats of tile at index j
   * @param {*} j the index of tile
   *
   */

  /**
   * fetch tiles of j tile from our server
   *
   * @param  {int} j the index of tile
   */
  const getXImage = async (providedImageDate) => {
    // Check tile limit for current workspace before adding
    if (workspaces[currentWorkspace].length >= tilesLimits.workspace_tiles) {
      toast.error(
        t("map.maxTiles", {
          max: tilesLimits.workspace_tiles,
          workspace: currentWorkspace,
        }),
      );
      return false;
    }

    const requestImageDate = !providedImageDate ? imageDate : providedImageDate;

    try {
      const tileParams = {
        index_id: currentIndex.id,
        image_date: requestImageDate,
        bounds: mapBounds,
      };
      const res = await getIndexTile(tileParams);

      // Can't connect to server
      if (!res) {
        toast.error(t("map.noServer"));
        return false;
      }

      if (res.status !== 200) {
        const errorMessage = getErrorMessage(
          { response: res },
          t("map.imageFailed"),
        );
        toast.error(errorMessage);
        return false;
      }

      // Success - store tile data
      dispatch(
        setTiles({
          tileId: newId(),
          tileUrl: res.data.tile_url_template,
          bbox: res.data.bounds,
          tileName: res.data.imagename_en,
          tileUrlExpiresAt: res.data.expires_at,
          // Kept so the signed tile URL can be re-minted before it expires.
          tileParams: tileParams,
        }),
      );
      return true;
    } catch (error) {
      console.error("Error fetching tiles:", error);
      const errorMessage = getErrorMessage(error, t("map.imageFailed"));
      toast.error(errorMessage);
      return false;
    }
  };

  /**
   * checks whether to fetch image from fao or our api
   * then fetch images of given indexes
   * @param  {int} polyIndexes int indexes of images to fetch
   */

  const handleClickSubmit = async () => {
    setSubmitLoading(true);
    try {
      await getXImage();
    } catch (error) {
      console.error("Error in handleClickSubmit:", error);
      const errorMessage = getErrorMessage(error, t("map.requestFailed"));
      toast.error(errorMessage);
    } finally {
      setSubmitLoading(false);
    }
  };

  /**
   * controls submit button enable state,
   * @returns boolean value to disable or enable button
   */
  const disableBtn = () => {
    if (submitLoading) return true;
    if (!imageDate) return true;
    if (dateError) return true;
    return false;
  };

  const remainingSlots =
    tilesLimits.workspace_tiles - (workspaces[currentWorkspace]?.length || 0);

  if (pageLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          height: "100vh",
          width: "100vw",
          bgcolor: "background.default",
          gap: 2,
        }}
      >
        <CircularProgress size={60} thickness={4} />
        <Typography variant="h6" color="text.secondary">
          {t("map.loading")}
        </Typography>
      </Box>
    );
  }

  return (
    <>
      <div className="monitoring-container">
        <div className="map-wrapper">
          {!drawingMode && (
            <NavigationPanel
              collapsed={leftFolded}
              onToggleCollapse={() => foldLeft(!leftFolded)}
            >
              {currentIndex?.id && (
                <>
                  <Divider sx={{ opacity: 0.5 }} />

                  <Box
                    sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}
                  >
                    {/* Label and availability text */}
                    <Box>
                      <Typography
                        variant="subtitle2"
                        sx={{
                          fontWeight: 800,
                          color: "primary.main",
                          fontSize: "0.75rem",
                          textTransform: "uppercase",
                          letterSpacing: "0.5px",
                        }}
                      >
                        {t("map.dateSelection")}
                      </Typography>
                      {indexMinDate && indexMaxDate && (
                        <Typography
                          variant="caption"
                          sx={{
                            display: "block",
                            color: "text.secondary",
                            fontSize: "0.7rem",
                          }}
                        >
                          {formatAvailableDateRange()}
                        </Typography>
                      )}
                    </Box>

                    {/* Date picker and submit button column */}
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 1.5,
                      }}
                    >
                      <AvailableDatePicker
                        availableDates={availableDates}
                        value={
                          imageDate ? new Date(imageDate) : indexMaxDate || null
                        }
                        defaultValue={indexMaxDate || null}
                        cleanable
                        style={{
                          width: "100%",
                          borderColor: dateError ? "#d32f2f" : undefined,
                        }}
                        className={dateError ? "date-error" : ""}
                        onChange={(date) => {
                          if (date) {
                            const selectedDate = new Date(date);
                            selectedDate.setHours(12, 0, 0, 0);
                            const selectedDateStr = selectedDate
                              .toISOString()
                              .split("T")[0];
                            // Only choose the date
                            dispatch(setImageDate(selectedDateStr));
                          } else {
                            dispatch(setImageDate(""));
                          }
                        }}
                        onClean={() => dispatch(setImageDate(""))}
                      />

                      <Button
                        variant="contained"
                        onClick={handleClickSubmit}
                        disabled={disableBtn()}
                        fullWidth
                        sx={{
                          height: "42px",
                          borderRadius: "10px",
                          textTransform: "none",
                          fontWeight: 700,
                          boxShadow: "0 4px 12px rgba(var(--primary-rgb), 0.2)",
                        }}
                      >
                        {submitLoading ? (
                          <CircularProgress size={20} color="inherit" />
                        ) : (
                          t("map.viewLayer")
                        )}
                      </Button>

                      {dateError && (
                        <Typography
                          variant="caption"
                          sx={{ color: "error.main", textAlign: "center" }}
                        >
                          {t("map.dateUnavailable")}
                        </Typography>
                      )}

                      <Button
                        variant="outlined"
                        onClick={() => setRangeDialogOpen(true)}
                        disabled={availableDates.length === 0}
                        fullWidth
                        sx={{
                          height: "36px",
                          borderRadius: "10px",
                          textTransform: "none",
                          fontWeight: 700,
                        }}
                      >
                        {t("map.selectRange")}
                      </Button>
                    </Box>
                  </Box>

                  {legends && legends.length > 0 && (
                    <>
                      <Divider sx={{ opacity: 0.5 }} />
                      <IndexLegend legends={legends} unit={unit} />
                    </>
                  )}
                </>
              )}
            </NavigationPanel>
          )}

          <MapComponent effectiveLayer={effectiveLayer} />
        </div>
      </div>

      {/* Panel switcher with Tiles and Field panels */}
      <PanelSwitcher collapsed={rightFolded} onCollapse={foldRight} />
      <MapLegend
        disableSatellite={disableSatellite}
        toggleLayer={toggleLayer}
      />

      {/* Tile Ban Banner */}
      {tileBan.banned && (
        <Box
          className="glass-panel"
          sx={{
            position: "fixed",
            left: "50%",
            transform: "translateX(-50%)",
            bottom: "76px", // above the map legend
            width: "320px",
            p: 1.5,
            zIndex: 1100,
          }}
        >
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              borderRadius: "16px",
              backgroundColor: "rgba(211, 47, 47, 0.08)",
              border: "1px solid rgba(211, 47, 47, 0.2)",
            }}
          />
          <Box sx={{ position: "relative", zIndex: 1 }}>
            <Typography
              variant="caption"
              sx={{
                display: "block",
                fontWeight: 700,
                color: "error.main",
                mb: 0.5,
                textTransform: "uppercase",
              }}
            >
              {t("map.ban.title")}
            </Typography>
            <Typography variant="body2" sx={{ color: "text.primary", mb: 1 }}>
              {tileBan?.message || t("map.ban.fallback")}
            </Typography>
            <Typography variant="body2" sx={{ color: "text.secondary", mb: 1 }}>
              {t("map.ban.liftIn", { time: formatedRemainingTime })}
            </Typography>
            <Button
              variant="contained"
              size="small"
              color="primary"
              fullWidth
              disabled={checkingBan || remainingSeconds !== 0}
              onClick={handleLiftBan}
            >
              {checkingBan ? t("map.ban.checking") : t("map.ban.liftNow")}
            </Button>
          </Box>
        </Box>
      )}

      <DateRangeDialog
        open={rangeDialogOpen}
        onClose={() => setRangeDialogOpen(false)}
        availableDates={availableDates}
        indexMinDate={indexMinDate}
        indexMaxDate={indexMaxDate}
        remainingSlots={remainingSlots}
        onAddLayer={getXImage}
      />
    </>
  );
}
